import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import DiscountHeader from './DiscountHeader';
import DiscountTable from './DiscountTable';
import DiscountForm from './DiscountForm';
import EditDiscountForm from './EditDiscountForm';
import 'react-toastify/dist/ReactToastify.css';


const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

const DiscountManager = () => {
  const [discounts, setDiscounts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [currentDiscount, setCurrentDiscount] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'Percentage',
    value: '',
    startDate: '',
    endDate: '',
    status: 'Active'
  });
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const fetchDiscounts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/list-discounts`);
        
        // Handle 404 "No discounts found" case explicitly
        if (response.status === 404) {
          setDiscounts([]);
          return;
        }
        
        if (!response.ok) {
          throw new Error('Failed to fetch discounts');
        }
        
        const data = await response.json();
        
        // Check if data.discounts exists and is an array
        if (data && data.discounts && Array.isArray(data.discounts)) {
          setDiscounts(data.discounts);
        } else if (Array.isArray(data)) {
          setDiscounts(data);
        } else {
          console.error('Unexpected response format:', data);
          setDiscounts([]);
        }
      } catch (err) {
        toast.error(`Error: ${err.message}`);
        setDiscounts([]);
      }
    };
    fetchDiscounts();
  }, [refreshKey]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddDiscount = () => {
    setFormData({
      name: '',
      code: '',
      type: 'Percentage',
      value: '',
      startDate: '',
      endDate: '',
      status: 'Active'
    });
    setShowAddForm(true);
  };

  const handleEditDiscount = (discount) => {
    setCurrentDiscount(discount);
    setFormData({
      name: discount.name,
      code: discount.code,
      type: discount.type,
      value: discount.value,
      startDate: discount.startDate,
      endDate: discount.endDate,
      status: discount.status || 'Active'
    });
    setShowEditForm(true);
  };

  const handleCreateSuccess = (newDiscount) => {
    setDiscounts(prev => Array.isArray(prev) ? [...prev, newDiscount] : [newDiscount]);
    setShowAddForm(false);
    toast.success('Discount created successfully!');
    setRefreshKey(prev => prev + 1);
  };

  const handleUpdateSuccess = (updatedDiscount) => {
    setDiscounts(prev => 
      Array.isArray(prev) 
        ? prev.map(d => d.id === updatedDiscount.id ? updatedDiscount : d)
        : [updatedDiscount]
    );
    setShowEditForm(false);
    toast.success('Discount updated successfully!');
    setRefreshKey(prev => prev + 1);
  };

  const handleApiError = (error) => {
    toast.error(error || 'Operation failed');
  };

  function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }
  
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <ToastContainer position="top-center" theme='colored' autoClose={5000} />
      <DiscountHeader onAddDiscount={handleAddDiscount} />
      <DiscountTable 
        discounts={discounts}
        onEdit={handleEditDiscount}
        refreshDiscounts={refreshKey}
      />
      
      {showAddForm && (
        <DiscountForm
          formData={formData}
          onInputChange={handleInputChange}
          onCancel={() => setShowAddForm(false)}
          onSuccess={handleCreateSuccess}
          onError={handleApiError}
        />
      )}
      
      {showEditForm && currentDiscount && (
        <EditDiscountForm
          formData={formData}
          onInputChange={handleInputChange}
          onCancel={() => setShowEditForm(false)}
          discountId={currentDiscount.id}
          onSuccess={handleUpdateSuccess}
          onError={handleApiError}
        />
      )}
    </div>
  );
};

export default DiscountManager;